/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import backendPropertiesCrud from "@services/housien/properties";

type Pagination = {
  page?: string | number;
  limit?: string | number;
};

type Filters = Record<string, string | number | boolean>;

type FetchAgencyPropertiesParams = {
  pagination?: Pagination;
  filters?: Filters;
};

/**
 * Default page size for the agency-mount property load.
 *
 * The legacy implementation hardcoded a limit of 10, silently capping the
 * global agency property slice. The default is now aligned with the backend
 * maximum page size so callers that do not specify a limit receive the full
 * working set rather than an arbitrary cap of ten.
 */
export const DEFAULT_AGENCY_PROPERTY_LIMIT = 200;

/**
 * Load the properties for an agency, returning them as an array.
 *
 * Pagination passed by the caller is forwarded verbatim; when omitted, a
 * sensible default page size is used instead of the legacy hardcoded cap of
 * ten. This is the single source of truth for the agency-mount property load.
 *
 * @param agencyId - The agency whose properties are being loaded.
 * @param params - Optional pagination and filters for the search.
 * @returns A promise resolving to the array of property values.
 */
export async function fetchAgencyProperties(
  agencyId: string,
  params: FetchAgencyPropertiesParams = {},
): Promise<any[]> {
  const pagination: Pagination = params.pagination ?? {
    page: 1,
    limit: DEFAULT_AGENCY_PROPERTY_LIMIT,
  };
  const response = await backendPropertiesCrud(agencyId).search({
    pagination,
    filters: params.filters ?? {},
  });
  const data = response?.data;
  if (!data) {
    return [];
  }
  return Object.values(data);
}
